import { useState, useRef, useEffect } from "react";
import { ShareNetwork, Check, WhatsappLogo, XLogo, LinkSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

const BASE = process.env.REACT_APP_BACKEND_URL;
const OG = {
  photo: (id) => `${BASE}/api/share/p/${id}`,
  album: (id) => `${BASE}/api/share/album/${id}`,
  atlas: (id) => `${BASE}/api/share/atlas/${id}`,
};

// One-tap share for any public photo / album / atlas. Uses crawler-friendly OG
// URLs so WhatsApp, X, etc. render a rich preview card.
export default function ShareButton({ id, type = "photo", title, className = "", variant = "icon" }) {
  const [open, setOpen] = useState(false);
  const [done, setDone] = useState(false);
  const ref = useRef(null);
  const url = (OG[type] || OG.photo)(id);
  const text = title || "PhotoPulse.pro";

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const onClick = async (e) => {
    e.preventDefault(); e.stopPropagation();
    if (navigator.share) {
      try { await navigator.share({ title: text, url }); return; } catch { /* fall through to menu */ }
    }
    setOpen((o) => !o);
  };
  const copy = async (e) => {
    e.stopPropagation();
    try { await navigator.clipboard.writeText(url); setDone(true); toast.success("Link copied"); setTimeout(() => setDone(false), 1800); }
    catch { toast.error("Could not copy"); }
    setOpen(false);
  };
  const openWin = (href, e) => { e.stopPropagation(); window.open(href, "_blank", "noopener"); setOpen(false); };

  return (
    <div className="relative inline-block" ref={ref}>
      <button type="button" data-testid={`share-${type}-${id}`} onClick={onClick} title="Share" aria-label="Share"
        className={variant === "button"
          ? `flex items-center gap-1.5 rounded-full bg-[#F27A54] px-4 py-2 text-sm font-bold text-white transition-transform hover:scale-105 ${className}`
          : `grid h-9 w-9 place-items-center rounded-full bg-black/50 text-white backdrop-blur transition-colors hover:bg-black/70 ${className}`}>
        {done ? <Check size={16} weight="bold" /> : <ShareNetwork size={16} weight="fill" />}
        {variant === "button" && <span>Share</span>}
      </button>
      {open && (
        <div data-testid={`share-menu-${id}`} className="absolute right-0 z-50 mt-2 w-44 overflow-hidden rounded-2xl border border-[#E5DFD3] bg-white py-1 shadow-[0_12px_40px_rgba(44,39,36,0.16)]">
          <button data-testid={`share-whatsapp-${id}`} onClick={(e) => openWin(`https://wa.me/?text=${encodeURIComponent(text + " " + url)}`, e)} className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-sm font-semibold text-[#2C2724] hover:bg-[#FFF3EC]"><WhatsappLogo size={17} weight="fill" className="text-[#25D366]" /> WhatsApp</button>
          <button onClick={(e) => openWin(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, e)} className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-sm font-semibold text-[#2C2724] hover:bg-[#FFF3EC]"><XLogo size={16} weight="fill" /> Post on X</button>
          <button data-testid={`share-copy-${id}`} onClick={copy} className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-sm font-semibold text-[#2C2724] hover:bg-[#FFF3EC]"><LinkSimple size={16} weight="bold" className="text-[#F27A54]" /> Copy link</button>
        </div>
      )}
    </div>
  );
}
